import { useEffect, useRef, useState } from 'react'
import { tonPlop, tonFalsch } from '../ton'
import { ballonsetFuer } from './ballonsets'

/**
 * Das Ballon-Format.
 *
 * Vier feste Quadranten, kein Wandern: Im Fernsehmodus schaut das Kind nach
 * oben zum Bild und tippt nach unten, ohne hinzusehen. Vier grosse Flaechen
 * trifft man blind, bewegte Ziele nicht.
 *
 * Gemessen wird die Zeit vom Erscheinen bis zur Beruehrung. Sichtbar ist
 * davon nichts, weder Uhr noch Balken.
 */
export function Ballons({
  antworten,
  loesung,
  gesperrt,
  aufgabenId,
  stoppuhr,
  setId,
  onAntwort,
}: {
  antworten: number[]
  loesung: number
  gesperrt: boolean
  /**
   * Eindeutige Kennung der Aufgabe.
   *
   * Frueher diente die Antwortfolge als Kennung. Das ging fast immer gut, aber
   * eben nur fast: Kamen zweimal hintereinander dieselben vier Zahlen in
   * derselben Reihenfolge, lief die Stoppuhr einfach weiter und die zweite
   * Antwort galt als quaelend langsam.
   */
  aufgabenId: string
  /** Wird hochgezaehlt, wenn die Messung neu beginnen soll (Rueckkehr aus dem Hintergrund). */
  stoppuhr: number
  setId: string
  onAntwort: (eingabe: number, zeitMs: number) => void
}) {
  const [getroffen, setGetroffen] = useState<number | null>(null)
  const [daneben, setDaneben] = useState<number | null>(null)
  const start = useRef(Date.now())
  const schonGetippt = useRef(false)
  const wackelUhr = useRef<number | null>(null)
  const farben = ballonsetFuer(setId)

  useEffect(() => {
    start.current = Date.now()
    schonGetippt.current = false
    setGetroffen(null)
    setDaneben(null)
  }, [aufgabenId, stoppuhr])

  useEffect(() => {
    return () => {
      if (wackelUhr.current !== null) window.clearTimeout(wackelUhr.current)
    }
  }, [])

  function tippen(wert: number, index: number) {
    // Der Ref sperrt sofort. Der Zustand allein wuerde zwei Beruehrungen im
    // selben Ereignisdurchlauf beide durchlassen.
    if (gesperrt || schonGetippt.current) return
    schonGetippt.current = true
    const zeitMs = Date.now() - start.current

    if (wert === loesung) {
      setGetroffen(index)
      tonPlop()
    } else {
      setDaneben(index)
      tonFalsch()
      if (wackelUhr.current !== null) window.clearTimeout(wackelUhr.current)
      wackelUhr.current = window.setTimeout(() => {
        wackelUhr.current = null
        setDaneben(null)
      }, 400)
    }

    onAntwort(wert, zeitMs)
  }

  return (
    <div className="ballons">
      {antworten.map((wert, i) => {
        const klassen = ['ballon']
        if (getroffen === i) klassen.push('ballon--getroffen')
        else if (getroffen !== null) klassen.push('ballon--entweicht')
        if (daneben === i) klassen.push('ballon--daneben')

        return (
          <button
            key={`${aufgabenId}-${i}`}
            className={klassen.join(' ')}
            onClick={() => tippen(wert, i)}
            aria-label={`Antwort ${wert}`}
            type="button"
          >
            <BallonKoerper
              farbe={farben.farben[i % 4]!}
              schrift={farben.schrift}
              wert={wert}
            />
            {getroffen === i && <Funken farbe={farben.farben[i % 4]!} />}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Ballon samt Zahl als ein SVG.
 *
 * Die Zahl gehoert hier hinein und nicht als HTML darueber: Der Ballonkoerper
 * sitzt in der oberen Haelfte der Zeichenflaeche, weil unten die Schnur Platz
 * braucht. Ein mittig im Tippfeld zentrierter Text landet dadurch zu tief,
 * und zwar je nach Fenstergroesse unterschiedlich weit. Im SVG haengt die
 * Zahl dagegen fest an der Ballongeometrie und sitzt auf jedem Geraet richtig.
 */
function BallonKoerper({
  farbe,
  schrift,
  wert,
}: {
  farbe: string
  schrift: string
  wert: number
}) {
  return (
    <svg className="ballon__koerper" viewBox="0 0 100 120" preserveAspectRatio="xMidYMid meet">
      <path
        d="M50 6 C74 6 88 24 88 45 C88 64 72 78 56 88 L44 88 C28 78 12 64 12 45 C12 24 26 6 50 6 Z"
        fill={farbe}
      />
      {/* Glanzlicht, damit der Ballon rund wirkt statt flach. */}
      <ellipse cx="34" cy="30" rx="11" ry="15" fill="#fff" opacity="0.32" />
      <path d="M44 88 L50 95 L56 88 Z" fill={farbe} />
      {/* Kurze Schnur. Eine lange wuerde den Ballon optisch nach oben
          schieben und damit aus der Mitte seiner Tippflaeche heraus. */}
      <path
        d="M50 95 q3 4 -1.5 7 q-4.5 3 -1.5 6"
        stroke={farbe}
        strokeWidth="2"
        fill="none"
        opacity="0.75"
      />
      {/*
        y = 59 statt der geometrischen Mitte 45: Die Baseline liegt unter der
        Textmitte, bei dieser Schriftgroesse rund 14 Einheiten. Bewusst ohne
        dominant-baseline, das unterstuetzen aeltere Smart-TV-Browser nicht
        zuverlaessig, und dann saesse die Zahl dort wieder daneben.
      */}
      <text
        x="50"
        y="59"
        textAnchor="middle"
        fontSize="40"
        fontWeight="800"
        fontFamily="inherit"
        fill={schrift}
      >
        {wert}
      </text>
    </svg>
  )
}

/** Partikel beim Platzen. Kurz genug, um nichts aufzuhalten. */
function Funken({ farbe }: { farbe: string }) {
  const stuecke = Array.from({ length: 10 }, (_, i) => {
    const winkel = (i / 10) * Math.PI * 2
    return {
      dx: `${Math.cos(winkel) * 14}vmin`,
      dy: `${Math.sin(winkel) * 14}vmin`,
    }
  })

  return (
    <span className="funken">
      {stuecke.map((s, i) => (
        <span
          key={i}
          className="funke"
          style={
            {
              background: i % 2 === 0 ? farbe : 'var(--gold)',
              '--dx': s.dx,
              '--dy': s.dy,
            } as React.CSSProperties
          }
        />
      ))}
    </span>
  )
}
